import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Grid,
    Typography,
    Box,
    Paper,
    useTheme,
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
} from 'recharts';
import axios from 'axios';
import Sidedash from './uicomponents/Sidedash';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const Dashboard = () => {
    const theme = useTheme();
    const [dashboardData, setDashboardData] = useState({
        thisMonthEarnings: 0,
        thisYearEarnings: 0,
        newStudentsThisMonth: 0,
        newStudentsThisYear: 0,
        renewalsThisMonth: 0,
        renewalsThisYear: 0,
        monthlyTrends: [],
        slotDistribution: [],
        dailyTrends: [],
        paymentModeDistribution: [],
        averageBookingValue: 0,
        totalBookings: 0,
        topSlots: [],
        renewalRate: 0,
        occupancyRate: 0,
        totalAvailableSlots: 0,
        currentBookedSlots: 0,
    });

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
                const response = await axios.get(`${backendUrl}/analytics/dashboard`, {
                    headers: {
                        'auth-token': localStorage.getItem('token')
                    }
                });
                setDashboardData(response.data);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            }
        };

        fetchDashboardData();
    }, []);

    const StatCard = ({ title, value, subtitle, isCurrency = false, trend = null, trendValue = 0, gradient = 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)', isPercentage = false }) => (
        <Card
            sx={{
                height: '100%',
                background: gradient,
                color: 'white',
                transition: 'transform 0.2s',
                '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                },
            }}
        >
            <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" color="inherit" sx={{ opacity: 0.9 }} gutterBottom>
                        {title}
                    </Typography>
                    {trend && (
                        <Box
                            sx={{
                                width: 45,
                                height: 45,
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.875rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {trend === 'up' ? '↑' : '↓'}{Math.abs(trendValue)}%
                        </Box>
                    )}
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'inherit', mb: 1 }}>
                    {isCurrency ? `₹${value.toLocaleString()}` : 
                     isPercentage ? `${value.toFixed(1)}%` :
                     typeof value === 'number' ? Math.round(value).toLocaleString() : value}
                </Typography>
                <Typography color="inherit" sx={{ opacity: 0.8 }}>
                    {subtitle}
                </Typography>
            </CardContent>
        </Card>
    );

    return (

        <div className="container-fluid">
      <div className="row">

        <Sidedash />
        <div className="col-md-9 pt-3 ps-4"><div className="row">
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" sx={{ color: theme.palette.primary.main }}>
                    Dashboard Overview
                </Typography>
            </Box>
            
            <Grid container spacing={3}>

                {/* First Row of Cards */}
                <Grid size={12} item xs={12}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 6, sm: 4, md: 3  }} item xs={12} sm={6} md={3}>
                            <StatCard
                                title="This Month's Earnings"
                                value={dashboardData.thisMonthEarnings}
                                subtitle="vs. last month"
                                isCurrency={true}
                                trend="up"
                                trendValue={14}
                                gradient="linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4, md: 3  }} item xs={12} sm={6} md={3}>
                            <StatCard
                                title="This Year's Earnings"
                                value={dashboardData.thisYearEarnings}
                                subtitle="vs. last year"
                                isCurrency={true}
                                trend="up"
                                trendValue={22}
                                gradient="linear-gradient(45deg, #1976D2 30%, #42A5F5 90%)"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4, md: 3  }} item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Average Booking Value"
                                value={dashboardData.averageBookingValue}
                                subtitle="Per booking"
                                isCurrency={true}
                                trend="up"
                                trendValue={8}
                                gradient="linear-gradient(45deg, #00BCD4 30%, #4DD0E1 90%)"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4, md: 3  }} item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Total Bookings"
                                value={dashboardData.totalBookings}
                                subtitle="All time"
                                trend="up"
                                trendValue={5}
                                gradient="linear-gradient(45deg, #009688 30%, #4DB6AC 90%)"
                            />
                        </Grid>
                    </Grid>
                </Grid>


                {/* Full Width Chart */}
                <Grid size={{ xs: 12, sm: 6, md: 4  }} item xs={12}>
                    <Paper 
                        sx={{ 
                            p: 3,
                            background: 'linear-gradient(to bottom right, #ffffff, #f5f5f5)',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Daily Revenue Trends
                        </Typography>
                        <Box sx={{ width: '100%', height: 400 }}>
                            <ResponsiveContainer>
                                <LineChart
                                    data={dashboardData.dailyTrends}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                                    <XAxis dataKey="date" stroke="#666" />
                                    <YAxis stroke="#666" />
                                    <Tooltip />
                                    <Legend />
                                    <Line 
                                        type="monotone" 
                                        dataKey="total" 
                                        stroke="#2196F3" 
                                        strokeWidth={2}
                                        dot={{ r: 4 }}
                                        activeDot={{ r: 6 }}
                                        name="Revenue" 
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    </Paper>
                </Grid>

                {/* Two Half Width Charts */}
                <Grid size={{ xs: 12, sm: 6, md: 4  }} item xs={12} md={6}>
                    <Paper 
                        sx={{ 
                            p: 3,
                            background: 'linear-gradient(to bottom right, #ffffff, #f5f5f5)',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            height: '100%'
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Monthly Revenue Trends
                        </Typography>
                        <Box sx={{ width: '100%', height: 350 }}>
                            <ResponsiveContainer>
                                <BarChart
                                    data={dashboardData.monthlyTrends}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                                    <XAxis dataKey="month" stroke="#666" />
                                    <YAxis stroke="#666" />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="total" fill="#2196F3" name="Revenue" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Paper>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4  }} item xs={12} md={6}>
                    <Paper 
                        sx={{ 
                            p: 3,
                            background: 'linear-gradient(to bottom right, #ffffff, #f5f5f5)',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            height: '100%'
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Payment Distribution
                        </Typography>
                        <Box sx={{ width: '100%', height: 350 }}>
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie
                                        data={dashboardData.paymentModeDistribution}
                                        dataKey="count"
                                        nameKey="mode"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={100}
                                        label
                                    >
                                        {dashboardData.paymentModeDistribution.map((entry, index) => (
                                            <Cell 
                                                key={`cell-${index}`} 
                                                fill={COLORS[index % COLORS.length]}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Box>
                    </Paper>
                </Grid>

                {/* Second Row of Cards */}
                <Grid size={12} item xs={12}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 6, sm: 4, md: 3  }} item xs={12} sm={6} md={3}>
                            <StatCard
                                title="New Students This Month"
                                value={Math.round(dashboardData.newStudentsThisMonth)}
                                subtitle="vs. last month"
                                trend="down"
                                trendValue={8}
                                gradient="linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4, md: 3  }} item xs={12} sm={6} md={3}>
                            <StatCard
                                title="New Students This Year"
                                value={Math.round(dashboardData.newStudentsThisYear)}
                                subtitle="vs. last year"
                                trend="up"
                                trendValue={15}
                                gradient="linear-gradient(45deg, #F57C00 30%, #FFA726 90%)"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4, md: 3  }} item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Renewal Rate"
                                value={dashboardData.renewalRate}
                                subtitle="Current period"
                                trend="up"
                                trendValue={15}
                                gradient="linear-gradient(45deg, #4CAF50 30%, #81C784 90%)"
                                isPercentage={true}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4, md: 3  }} item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Occupancy Rate"
                                value={dashboardData.occupancyRate}
                                subtitle="Current capacity"
                                trend="up"
                                trendValue={12}
                                gradient="linear-gradient(45deg, #9C27B0 30%, #BA68C8 90%)"
                                isPercentage={true}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                

                

                {/* Third Row of Cards */}
                <Grid size={12} item xs={12}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 6, sm: 4, md: 3  }} item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Total Available Slots"
                                value={Math.round(dashboardData.totalAvailableSlots)}
                                subtitle="All seats"
                                gradient="linear-gradient(45deg, #E91E63 30%, #F48FB1 90%)"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4, md: 3  }} item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Currently Booked Slots"
                                value={Math.round(dashboardData.currentBookedSlots)}
                                subtitle="In use"
                                gradient="linear-gradient(45deg, #C2185B 30%, #EC407A 90%)"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4, md: 3  }} item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Renewals This Month"
                                value={Math.round(dashboardData.renewalsThisMonth)}
                                subtitle="This month"
                                trend="up"
                                trendValue={10}
                                gradient="linear-gradient(45deg, #673AB7 30%, #9575CD 90%)"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4, md: 3  }} item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Renewals This Year"
                                value={Math.round(dashboardData.renewalsThisYear)}
                                subtitle="This year"
                                trend="up"
                                trendValue={18}
                                gradient="linear-gradient(45deg, #3F51B5 30%, #7986CB 90%)"
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            </div>
            </div>
        </div>
        </div>
    );
};

export default Dashboard; 